import { Location } from "@angular/common";
import { ChangeDetectorRef, Component, ElementRef, Input, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { ActivatedRoute, NavigationEnd, NavigationSkipped, Router } from "@angular/router";
import {
  CommunitiesService,
  computeParticipationGate,
  environment,
  EventBusService,
  FSP_COMMUNITY_STRING_ID,
  HelperService,
  LanguageService,
  ProductRequirement,
  ProductService,
  resolveLocalizedString,
  UserService,
  WindowService,
  sanitizeAccentColor,
  communitiesHubEnabled,
} from "@mypxplat/xplat/core";

import { CommunityBaseComponent } from "@mypxplat/xplat/features";
import { AppService, AvoService, BetaService, FirebaseService, HUB_POST_BACK_KEY, SeoService, WebCommunityService } from "@mypxplat/xplat/web/core";
import { catchError, filter, take, takeUntil } from "rxjs/operators";
import { forkJoin, of } from "rxjs";
import { OnInit, OnDestroy } from "@angular/core";
import { CommunitySearchComponent } from "../community-search/community-search.component";
import { CommunitySettingsComponent, SimpleSignupComponent } from "../modals";
import { ChannelComponent } from "./channel/channel.component";
import { communityHasOnboardingFlow } from "./onboarding-wizard/community-onboarding-wizard.component";
import { DiscordMigrationWelcomeComponent } from "../modals/discord-migration-welcome/discord-migration-welcome.component";
import { DiscordClaimComponent } from "../modals/discord-claim/discord-claim.component";
import { FuiContentFilterComponent, FuiSidebarFilterComponent, FuiFilterContentComponent, FilterItem } from "../fender-ui";
import { BreadcrumbItem } from "../fender-ui/layout-header/layout-header.component";
import { BetaCommunityService, UnreadCandidates } from "@mypxplat/xplat/core";
import { TranslateService } from "@ngx-translate/core";

/**
 * Query params the community view does not own but must not destroy.
 *
 * `updateQueryParams` rebuilds the query string from the view's own state, so anything not
 * listed here is dropped on the next channel or section change. An allow-list rather than a
 * blanket merge because the rebuild is what REMOVES a cleared filter or search term — merging
 * would make those sticky.
 */
const PRESERVED_QUERY_PARAMS = ["discordMigration", "discordLinkSuccess"];

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-community",
  templateUrl: "community.component.html",
  standalone: false,
})
export class CommunityComponent extends CommunityBaseComponent implements OnInit, OnDestroy {
  public breadcrumbs: BreadcrumbItem[] = [];
  @Input() public betaStringId: string;
  /**
   * Which community this instance renders. Defaults to Fender Studio Pro so any existing
   * `<myp-community>` usage without a `[communityStringId]` binding keeps working. Passed
   * through to child components (channel, comment, create-post-input) — DENG-727 Phase 3.
   */
  @Input() public communityStringId: string = "fender-studio-pro";

  /**
   * Page heading + description rendered by `<fui-layout-header>`. Both start null and
   * are populated by `applyCommunityHeading()` once `CommunitiesService` resolves the
   * community. Until that happens, the layout header renders skeleton loaders — no
   * hardcoded FSP defaults, no risk of flashing FSP copy on a non-FSP community while
   * its config loads.
   */
  public communityHeading: string | null = null;
  public communityDescription: string | null = null;
  /**
   * Flips true once `applyCommunityHeading` finds the community in the config cache.
   * Drives `<fui-layout-header>[loading]` so the heading/description slots show skeleton
   * loaders during the load window and swap to real values on the same render tick.
   */
  public communityConfigLoaded: boolean = false;
  /**
   * Optional hero image drawn as the background of the whole top section (breadcrumb +
   * heading + nav pills + content filter, up to the trailing `<hr>`). Set from
   * `community.branding.hero_image_url` in `applyCommunityHeading()`. Reusable pattern —
   * `.fui-hero-section` in `_fender-ui.scss` is what actually renders it.
   */
  public communityHeroImageUrl: string | null = null;

  /**
   * Which theme to apply to the hero section, independent of the viewer's global theme.
   * "light" = light theme (dark text on the image); "dark" = dark theme (light text).
   * Null = inherit the viewer's global theme. Rendered as `theme_plainlight` /
   * `theme_plaindark` on the hero container.
   */
  public communityHeroTheme: "light" | "dark" | null = null;
  /**
   * The community's brand colour (`branding.accent_color`), as `--community-accent` on the
   * feed + pane row — the unread dots read it (_community.scss). Null falls back to the
   * stylesheet's blue. Only plain colour values get through, since it lands in a style binding.
   */
  public communityAccent: string | null = null;

  @ViewChild("communityColumnLayoutContainer") communityColumnLayoutContainer: ElementRef;
  @ViewChild("contentContainer") contentContainer: ElementRef;
  @ViewChild("searchCmp") searchCmp: CommunitySearchComponent;
  @ViewChild("channelCmp") channelCmp: ChannelComponent;
  @ViewChild("fuiSidebarFilter") fuiSidebarFilter: FuiSidebarFilterComponent;
  @ViewChild("fuiSidebarFilterBeta") fuiSidebarFilterBeta: FuiSidebarFilterComponent;
  @ViewChild("fuiContentFilter") fuiContentFilter: FuiContentFilterComponent;
  @ViewChild("fuiContentFilterBeta") fuiContentFilterBeta: FuiContentFilterComponent;
  /** The post detail when it takes the whole column — used to scroll its top into view. */
  @ViewChild("fullPageDetail", { read: ElementRef }) fullPageDetail: ElementRef;
  public displayedSection: string = "channels";
  public channelID: string;
  public topicID: string;
  public postID: any;
  public scrollToPostID: any;

  public topics: Array<any>;
  public allTopics: Array<any>;
  public showingAllTopics: boolean = false;
  public selectedTopic: any;

  public inSetupMode: boolean = false;
  /** One welcome per page life — initView can run more than once. */
  private _discordMigrationShown = false;
  /**
   * The quiet "Here from Discord?" link in the header. Only on the Tone Master Pro community,
   * only while linking is switched on, and only until they have actually linked.
   */
  public showDiscordMigrationLink = false;
  public tagline: string;
  public loading: boolean = true;
  public postsLoading: boolean;
  public shouldShowMemberResults: boolean = false;
  public searchResults: any;

  public skillsAcknowledged: boolean = false;

  public shouldShowPostDetails: boolean = false;
  public postDetail: any;
  public postDetailReplies: Array<any>;

  // --- Split-pane post detail -------------------------------------------------------
  //
  // In list view on a wide viewport, opening a post renders it beside the feed instead of
  // navigating to its own page. The URL still becomes the post's own, so the pane is
  // shareable and deep-linkable; `PostDetailPageComponent` is no longer reachable for
  // community posts — the same route now renders this shell (see community-page.module).

  /** Post shown in the right pane; falsy means the pane is closed. */
  public selectedPostId: string | null = null;
  /** Pane width in px. Seeded to half the row and then dragged; persisted per browser. */
  public paneWidth = 0;
  /** True when the layout can host a pane at all — list view, wide enough, not beta. */
  public splitPaneActive = false;
  /**
   * A post is open on a layout with no pane, so the detail takes the whole column. Derived
   * from the two above rather than recomputed in the template — it gates three separate
   * things (the detail, the hidden feed, the feed's skeletons) and they must agree.
   */
  public showFullPageDetail = false;
  /**
   * Narrower than this and there is simply no room for two columns, so a post opens
   * full-page instead — the row has to fit the channels rail, a usable feed, a usable pane
   * and the layout's 70px gutters. 900px was the first guess and was far too low: it left
   * the two columns about 285px each.
   *
   * 1100 rather than the earlier 1200 because the rail was narrowed 330px → 230px: at 1100
   * the feed and pane share 800px, which still clears FEED_MIN + PANE_MIN with room over.
   *
   * This is the ONE breakpoint the community layout turns on, and everything that used to
   * key off the rail's own 900px now keys off this instead — the rail's `display` (see
   * `.community-channel-rail` in `_community.scss`) and `toggleFilters()` below. They used
   * to disagree, which put the rail beside a full-width post detail between 900 and 1100:
   * reading a post with the channel list still taking a third of the row.
   */
  private readonly SPLIT_MIN_VIEWPORT = 1100;
  /** Keeps both columns usable no matter how far the divider is dragged. */
  private readonly PANE_MIN_WIDTH = 320;
  private readonly FEED_MIN_WIDTH = 420;
  /** Below this the feed's rows drop their reaction controls — see `compactList`. */
  private readonly FEED_COMPACT_WIDTH = 520;
  /** Feed rows are too narrow for reaction pills; they stay available in the detail. */
  public compactList = false;

  public query;
  public selectedQuery;

  public betaProgram: any;
  public isBetaManager: boolean = false;
  public filters: any = {
    type: "all",
    status: "all",
    reviewStatus: "all",
    author: "all",
    jiraTicketNumber: "",
    sort: "date",
  };
  public betaAnnouncements: Array<any>;
  public showTags: boolean = true;

  public browsingSkillId: string;
  public communityQuery: string;
  public selectedBetaView = this.betaCommunityService.getViewMode() || "list";
  public announcementsCollapsed: boolean;
  public betaViewOptions: Array<any> = [];
  public channelsWithBadges: FilterItem[] = [];

  // --- New-post indicators ------------------------------------------------------------

  /** Unread threads per channel id, driving the rail's badge and its bold title. */
  public channelUnread: Record<string, number> = {};
  /** What the last fetch found, recounted against your view records as you read (`_applyChannelUnread`). */
  private _unreadCandidates: UnreadCandidates | null = null;
  /** Precomputed for the template — "Mark all read" only exists when there is something to mark. */
  public hasUnreadChannels = false;

  /**
   * Which community `channelUnread` was fetched for.
   *
   * The fetch is page-load only by decision (reading recounts it locally), and `updateChannelsWithBadges()` — the hook
   * this hangs off — also fires on every feed load. Without this the rail would re-query
   * Algolia every time a channel's posts came back.
   */
  private _unreadFetchedFor: string | null = null;
  /** Set when the post open on arrival came from the Communities hub — see `closeFullPageDetail`. */
  private _hubReturnUrl: string | null = null;

  constructor(
    userService: UserService,
    private _router: Router,
    private _win: WindowService,
    private _activatedRoute: ActivatedRoute,
    private _eventbusService: EventBusService,
    public appService: AppService,
    public webCommunityService: WebCommunityService,
    public communityService: WebCommunityService,
    public location: Location,
    public avoService: AvoService,
    public cd: ChangeDetectorRef,
    public betaService: BetaService,
    public betaCommunityService: BetaCommunityService,
    public helperService: HelperService,
    public productService: ProductService,
    public fbService: FirebaseService,
    private translate: TranslateService,
    private communitiesService: CommunitiesService,
    private languageService: LanguageService,
    private _seo: SeoService
  ) {
    super(userService, communityService);
    this.announcementsCollapsed = this.betaCommunityService.getAnnouncementsCollapsed(false);
    this.updateBetaViewOptions();
  }

  /**
   * Populate `communityHeading` / `communityDescription` from the loaded community.
   * FSP stays on its i18n keys (unchanged prod behavior); non-FSP communities get their
   * `title` + a plain-text version of `branding.welcome_html` (the layout-header renders
   * description as text, not HTML).
   */
  private applyCommunityHeading() {
    if (!this.communityStringId) return;
    const community = this.communitiesService.getCommunity(this.communityStringId);
    if (!community) return;

    // Hero image applies to FSP too — the whole point of the branding field is that an
    // admin can set an image on any community, FSP included. Passed to `<fui-layout-header>`
    // via `[heroImageUrl]` / `[heroTheme]` inputs — the layout-header handles the
    // `body.ghost-nav-route` + `showGhostNav` + scroll-listener wiring itself.
    this.communityHeroImageUrl = community.branding?.hero_image_url || null;
    this.communityHeroTheme = community.branding?.hero_theme || null;
    this.communityAccent = sanitizeAccentColor(community.branding?.accent_color);

    if (community.title) this.communityHeading = community.title;
    // Flag the header as loaded regardless of whether title/description are present —
    // an empty description is a valid state and the layout header handles it. What we
    // want to signal here is "config is resolved, stop the skeleton".
    this.communityConfigLoaded = true;
    // Re-render the breadcrumb trail now that we know the community's real title —
    // the multi-community trail is `Home → Communities → <title>` and the title
    // needs to land in the last crumb.
    this.refreshBreadcrumbs();

    // Prefer the first-class `description` map (per-locale, admin-editable). Fall back to
    // a stripped `branding.welcome_html` for communities that predate the description
    // field. Empty string on both = clear the FSP i18n-key fallback so we don't show FSP
    // copy on Tone Master Pro / Guitars / etc.
    const lang = this.languageService.selectedLanguage?.code || "en";
    const description = resolveLocalizedString(community.description, lang, "");
    if (description) {
      this.communityDescription = description;
    } else {
      const welcomeHtml = community.branding?.welcome_html;
      this.communityDescription = welcomeHtml
        ? welcomeHtml
            .replace(/<[^>]*>/g, " ")
            .replace(/\s+/g, " ")
            .trim()
        : "";
    }
    this.applyCommunitySeoMeta(community);
    this.cd.detectChanges();
  }

  /**
   * Emit SEO meta for the community feed page. Wave 1 of docs/community-seo-plan.md —
   * feed-primary strategy. Bots landing here need a real `<title>` + description + canonical
   * so search referrals rank; the same prerender pass captures the visible post-card
   * anchors that drive crawler discovery of individual post URLs.
   *
   * Skipped for beta communities (never public) and when `communityPublicAccess` is off
   * (mirrors the guard in `<myp-community-post-detail>._applySeo`).
   *
   * JSON-LD emits both a `WebSite` (name + URL for search-appearance) and a `BreadcrumbList`
   * (Home → Communities → {Community Title}) so Google can show the crumb trail in results.
   */
  private applyCommunitySeoMeta(community: any) {
    if (!this.seoOwnsPageTitle()) return;
    const communityTitle = community?.title || this.communityStringId;
    const rawDescription = this.communityDescription || `Join the ${communityTitle} community on MyFender. Ask questions, share ideas, and connect with other members.`;
    const trimmedDescription = rawDescription.length > 200 ? rawDescription.slice(0, 197).trimEnd() + "…" : rawDescription;
    const canonical = `https://my.fender.com/community/${this.communityStringId}/channels/all_posts`;
    this._seo.setPageMeta({
      title: `${communityTitle} Community | MyFender`,
      description: trimmedDescription,
      canonical,
      ogImage: community?.branding?.hero_image_url || undefined,
      ogType: "website",
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: `${communityTitle} Community`,
          url: canonical,
        },
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://my.fender.com/home" },
            { "@type": "ListItem", position: 2, name: "Communities", item: "https://my.fender.com/communities" },
            { "@type": "ListItem", position: 3, name: `${communityTitle} Community`, item: canonical },
          ],
        },
      ],
    });
  }

  /**
   * Build the layout-header breadcrumb trail. Multi-community mode routes the middle
   * crumb through `/communities` (the grid landing) so users can back out to the full
   * list of communities — otherwise the crumbs collapse to the historical single-item
   * trail. Beta community keeps its own trail (upstream `<myp-beta-community>` sets
   * `breadcrumbs` on its own) and is not touched here.
   *
   * The last crumb is the community's actual title once `applyCommunityHeading()` has
   * resolved it; before that (or on beta) it falls back to the FSP i18n key so the
   * skeleton state doesn't flash an empty final crumb.
   */
  private refreshBreadcrumbs() {
    if (this.betaStringId) return;
    if (environment.features.multiCommunity) {
      const lastLabel = this.communityHeading || "com.community";
      this.breadcrumbs = [{ label: "header.nav.home", routerLink: ["/home"] }, { label: "Communities", routerLink: ["/communities"] }, { label: lastLabel }];
    } else {
      this.breadcrumbs = [{ label: "header.nav.home", routerLink: ["/home"] }, { label: "com.community" }];
    }
  }

  private updateBetaViewOptions() {
    this.betaViewOptions = [
      {
        name: "Detailed",
        translationKey: "com.display_detailed",
        id: "detailed",
        selected: this.selectedBetaView == "detailed",
      },
      {
        name: "List",
        translationKey: "com.display_list",
        id: "list",
        selected: this.selectedBetaView == "list",
      },
    ];
    this.updateChannelsWithBadges();
  }

  /**
   * Re-evaluate the split layout when the window crosses the breakpoint. Bound here rather
   * than with @HostListener so it can be removed explicitly in ngOnDestroy — this component
   * outlives a lot of navigation.
   */
  private _onWindowResize = () => {
    const wasActive = this.splitPaneActive;
    const hadPost = !!this.selectedPostId;
    this.updateSplitPaneLayout();
    // Narrowing past the breakpoint with a post open snaps back to the LIST, it does not
    // promote the post to full width. Two reasons: shrinking a window is a request for less,
    // not for a different page — and the full-width detail reads its id from the route
    // snapshot, which `location.go` never updated, so it would render an empty "Post by …".
    if (wasActive && !this.splitPaneActive && hadPost) this.closePostPane();
    if (wasActive !== this.splitPaneActive) this.cd.detectChanges();
  };

  ngOnInit() {
    // Arrived on a post from the Communities hub (narrow screens open it here, full page):
    // its back goes to the hub as it was. Read now — the channel list nulls the cache as it
    // loads.
    const back = this.webCommunityService.communityListBackCache;
    if (back?.key === HUB_POST_BACK_KEY && back.returnUrl) {
      this._hubReturnUrl = back.returnUrl;
      this.webCommunityService.communityListBackCache = null;
    }
    this.isBetaManager = this.user?.groups?.length ? this.user.groups.some((group: any) => group.name === "beta_manager") : false;
    // Warm the community-config cache once for every community route. This is the single
    // place that fires `GET /communities` for the feature. Leaves like create-post-input
    // subscribe to `communities$` and read `getCommunity(id)?.features` whenever it lands
    // — safe whether the cache warms before or after the child mounts.
    if (!this.betaStringId) {
      this.communitiesService.load();
      this.communitiesService.communities$.pipe(takeUntil(this.destroy$)).subscribe(() => this.applyCommunityHeading());
      // A post read here or in the pane, its view records landing, or "Mark all read" moving
      // the floor: recount the rail's badges from what was already fetched — no query.
      this.webCommunityService.postViews$.pipe(takeUntil(this.destroy$)).subscribe(() => this._applyChannelUnread());
      // The settings modal (header gear or the composer's signature link) can change this
      // community's ordering. Reload the feed in the new order rather than leaving it until a
      // refresh. A search ignores the ordering, so it has nothing to reload, and any other
      // section picks the new ordering up when the feed next loads.
      this.webCommunityService.communitySortModeChanged$
        .pipe(
          filter(
            (change) =>
              // This community, or the default for all of them when this one has no override.
              (change.stringId === this.communityStringId || (change.stringId === null && !this.webCommunityService.profile?.communitySortModes?.[this.communityStringId])) &&
              !this.query &&
              this.displayedSection === "channels" &&
              !!this.channelCmp
          ),
          takeUntil(this.destroy$)
        )
        .subscribe(() => {
          this.appService.scrolledContentOffsets.community = undefined;
          this.channelCmp.initPosts();
        });
    }
    this.buildFilters();
    this.refreshBreadcrumbs();
    if (this.userService.user?.banned) {
      this._router.navigate(["../home"]);
    } else {
      this.initView();
      // this allows you to "navigate" to another community/section from this page
      let lastPath = this._router.url.split("?")[0];
      if (typeof window !== "undefined") window.addEventListener("resize", this._onWindowResize);
      // Opening and closing a post moves the URL with `location.go`, so the browser's own
      // back / forward (and the hardware back button on a phone) walks between URLs the
      // router never navigated to and therefore ignores. `Location.subscribe` fires on
      // popstate regardless, which is what keeps the view honest against the address bar.
      this._locationSub = this.location.subscribe((event: any) => {
        this._syncPostFromUrl(event?.url ?? this.location.path());
      });
      this._router.events.pipe(takeUntil(this.destroy$)).subscribe((result) => {
        // Closing a post swaps the URL with `location.go`, which the router never sees — so
        // it still believes it is on that post, and a link back to it (the detailed view's
        // timestamp `routerLink`) is discarded as a same-URL navigation. That click has to
        // do something, so honour it here.
        if (result instanceof NavigationSkipped) {
          const skippedPostId = this._postIdFromUrl(result.url);
          if (skippedPostId && skippedPostId !== this.selectedPostId) this.openSelectedPost(skippedPostId);
          return;
        }
        if (!(result instanceof NavigationEnd)) return;
        const path = result.urlAfterRedirects.split("?")[0];
        // Ignore query-param-only changes: it is the same view, and re-initialising it is
        // actively harmful. `runInit` sets `postsLoading = true`, but the channel component
        // is already loaded and has nothing new to emit — so `postsLoaded` never fires again
        // and the skeleton loaders sit on top of real posts forever. Stripping a one-shot
        // marker from the URL (see _openDiscordModal) is exactly such a navigation.
        if (path === lastPath) return;
        lastPath = path;
        this.initView();
      });
    }
  }

  initView() {
    const runInit = () => {
      this.displayedSection = this._activatedRoute.snapshot.params["category"];
      // Deep link straight at a post: /community/<communityId>/post/<postId>. That route now
      // renders THIS shell rather than a standalone page, so the feed loads underneath and
      // the post opens on top of it — in the right pane where the layout allows, and
      // full-width (today's page, chrome and all) where it doesn't.
      this.updateSplitPaneLayout();
      const routePostId = this._activatedRoute.snapshot.params["id"];
      let revealDeepLinkedPost = false;
      if (routePostId && !this.displayedSection) {
        this.displayedSection = "channels";
        this.selectedPostId = String(routePostId);
        this.showFullPageDetail = !this.splitPaneActive;
        // Full width, the community hero sits above the post and fills a phone screen — the
        // member tapped a link to a post and would land looking at the banner.
        revealDeepLinkedPost = this.showFullPageDetail;
      } else if (!routePostId) {
        // Navigated somewhere that is not a post — make sure a pane left open from a
        // previous view doesn't linger.
        this.selectedPostId = null;
        this.showFullPageDetail = false;
      }
      if (!this.displayedSection && this.betaStringId) this.displayedSection = "betadetails";
      // Skeletons only when there is genuinely nothing to show. Opening a post re-runs this
      // init (the URL changes), and flipping the feed back to "loading" when it is already
      // populated left the skeletons up for good: the channel component sees no channel
      // change, so it never reloads, so `postsLoaded` never fires to turn them off again.
      const feedAlreadyPopulated = !!this.communityService.activeParentPosts?.length;
      if (this.displayedSection != "betadetails" && this.displayedSection != "connect" && !this.communityService.restoringFromPostDetail && !feedAlreadyPopulated) {
        this.postsLoading = true;
      }
      if (this.betaStringId) {
        // get beta details.
        this.communityService.getBetaCommunityDetails(this.betaStringId);
        this.initBetaAnnouncements();
      }
      this.channelID = this._activatedRoute.snapshot.params["channelid"] && this.displayedSection == "channels" ? this._activatedRoute.snapshot.params["channelid"] : undefined;
      if (this.communityService.selectedChannel) this.channelID = this.communityService.selectedChannel.id;
      this.topicID = this._activatedRoute.snapshot.params["channelid"] && this.displayedSection == "topics" ? this._activatedRoute.snapshot.params["channelid"] : false;
      this.browsingSkillId = this._activatedRoute.snapshot.params["channelid"] && this.displayedSection == "connect" ? this._activatedRoute.snapshot.params["channelid"] : undefined;
      this.init();
      // Show the onboarding wizard when ALL of:
      //   1. This isn't a beta community.
      //   2. The community they're viewing has a wizard flow registered in the code
      //      (see `communityHasOnboardingFlow` — the compile-time registry).
      //   3. The user hasn't completed setup for THIS community yet.
      //      Uses `hasSetupCommunityFor(stringId)` — not the flat `hasSetupCommunity`
      //      boolean — so a user who's already run one community's wizard still sees
      //      the wizard on their first visit to another.
      //   4. The user is signed in AND eligible to participate in this community.
      //      Delegated to `computeParticipationGate`, the same "can this user post
      //      here?" helper that create-post-input uses — so wizard eligibility stays
      //      in lockstep with the community's `participation_condition` (signed-out,
      //      needs-FSP-sub, needs-product, needs-group, etc.) without duplicating rules.
      //      `profilePublic: true` passed unconditionally — the wizard is what SETS
      //      profilePublic to true, so gating on it would be circular.
      // Arrivals from the Discord announcement get the migration welcome instead of the wizard —
      // see `_handleDiscordMigration`. The param stays in the URL on purpose: both round trips
      // this modal can start (login, and Discord OAuth) come back to it, and the modal has to
      // reopen when they do.
      const fromDiscordMigration = `${this._activatedRoute.snapshot.queryParams["discordMigration"] || ""}` === "true";
      // Set by the Discord OAuth return (see ConnectionsComponent.handleReturnParams). Takes
      // precedence over the welcome: they have just linked, so re-asking them to link is the
      // confusing part this replaces.
      const justLinkedDiscord = `${this._activatedRoute.snapshot.queryParams["discordLinkSuccess"] || ""}` === "true";
      this._resolveDiscordMigrationLink();

      const hasFlow = communityHasOnboardingFlow(this.communityStringId);
      const alreadySetUp = this.webCommunityService.hasSetupCommunityFor(this.communityStringId);
      const gate = computeParticipationGate({
        community: this.communitiesService.getCommunity(this.communityStringId),
        user: this.user,
        profilePublic: true,
        subDetails: this.userService.subDetails,
        userGroups: Array.isArray(this.user?.groups) ? this.user.groups.map((g: any) => (typeof g === "string" ? g : g?.name)).filter(Boolean) : undefined,
        ownsProduct: (req: ProductRequirement) => this.productService.ownsProductRequirement(req),
      });
      // With the hub on, its own onboarding (`<myp-communities-onboarding>`) replaces these
      // per-community wizards.
      if (!communitiesHubEnabled(this.userService.checkEmployeeAccess(this.user)) && !this.betaStringId && hasFlow && !alreadySetUp && gate.ok && !fromDiscordMigration && !justLinkedDiscord)
        this.inSetupMode = true;
      if (justLinkedDiscord) this._handleDiscordClaim();
      else if (fromDiscordMigration) this._handleDiscordMigration();

      if (this._activatedRoute.snapshot.queryParams["postid"]) {
        this._router.navigate(["community/post/" + this._activatedRoute.snapshot.queryParams["postid"]], { queryParamsHandling: "merge" });
      } else if (this._activatedRoute.snapshot.queryParams["scrollToPost"]) {
        this.scrollToPostID = this._activatedRoute.snapshot.params["scrollToPost"];
      }
      this.updateCommunityTitle();
      this.cd.detectChanges();
      if (revealDeepLinkedPost) this._revealFullPageDetail();
    };

    if (this.user) {
      // Wait for the community profile so the wizard and participation gate see real state —
      // but never wait forever. `getFirebaseProfile` resolves FALSE and emits nothing when the
      // `user_profiles` document does not exist, which is exactly the case for an account
      // created moments ago: the doc is written by the onboarding wizard or by participating,
      // so a brand-new member arriving straight at a community has none. Without the fallback
      // the page sits on its loading skeletons indefinitely and nothing that runs inside
      // `runInit` — the wizard, the Discord migration modal, the posts themselves — ever fires.
      let started = false;
      const start = () => {
        if (started) return;
        started = true;
        runInit();
      };
      this.communityService.firebaseProfile$
        .pipe(
          filter((profile) => profile !== null),
          take(1),
          takeUntil(this.destroy$)
        )
        .subscribe(start);
      // Resolves truthy once the stream above has already fired, so `started` makes this a
      // no-op in the normal case and the rescue only in the missing-document one.
      this.communityService.getFirebaseProfile().then(
        (profile) => {
          if (!profile) start();
        },
        () => start()
      );
    } else {
      runInit();
    }
    this._eventbusService.observe(this._eventbusService.types.betaAnnouncementUpdated).subscribe((result) => {
      this.initBetaAnnouncements();
    });
  }

  /**
   * Opens the Discord migration welcome, once per page life.
   *
   * Marking the community as set up is deliberate and is the whole reason the wizard is
   * skipped: following a link from the Discord announcement IS the opt-in the wizard's first
   * step asks for, so re-asking is noise. Without this write the wizard would simply ambush
   * them on their next visit, once the query param is gone — which would be worse than
   * showing it now.
   *
   * The profile-visibility choice the wizard would have collected is left at its default;
   * members can change it from their profile.
   */
  private _handleDiscordMigration() {
    if (this._discordMigrationShown) return;
    this._discordMigrationShown = true;

    if (this.user && !this.webCommunityService.hasSetupCommunityFor(this.communityStringId)) {
      this.webCommunityService.markCommunitySetupComplete(this.communityStringId).catch(() => {});
    }

    this._openDiscordModal(DiscordMigrationWelcomeComponent);
  }

  /**
   * Opens the claim modal after a successful Discord link, once per page life.
   *
   * Marks the community as set up for the same reason the welcome does: someone who has linked
   * their Discord account from the migration flow has opted in far more explicitly than the
   * wizard's first step asks for, and the wizard would otherwise ambush them once the query
   * params are gone.
   */
  private _handleDiscordClaim() {
    if (this._discordMigrationShown) return;
    this._discordMigrationShown = true;

    if (this.user && !this.webCommunityService.hasSetupCommunityFor(this.communityStringId)) {
      this.webCommunityService.markCommunitySetupComplete(this.communityStringId).catch(() => {});
    }

    this._openDiscordModal(DiscordClaimComponent);
  }

  /**
   * Whether to offer the "Here from Discord?" link — hidden once they have linked, since by
   * then it has nothing left to tell them.
   *
   * Read from the user object rather than `GET /oauth/discord/link`: `fender_user_details`
   * already carries `discord-id` once the account is linked, so the answer is in memory and
   * costs nothing. An earlier version made that request on every community page load, which
   * was a round trip per visit to answer a question we already had locally.
   *
   * Hyphenated key, because `fender_user_details` is Fender's user record flattened as-is —
   * `discord-id` there is the same record type paeapi writes to the users table.
   */
  private _resolveDiscordMigrationLink() {
    const eligible = this.communityStringId === "tone-master-pro" && !!(environment.features as any)?.discordLinking;
    // Signed out: `user` is undefined, so the link shows — which is right, they may well be
    // arriving from the announcement.
    const alreadyLinked = !!this.user?.fender_user_details?.["discord-id"];
    this.showDiscordMigrationLink = eligible && !alreadyLinked;
  }

  /**
   * Opens the claim modal, which reads its own state and covers every case — signed out, not
   * linked, hardware still to register, ready to claim, already claimed.
   *
   * Deliberately does NOT look the member up first to decide which modal to show: the modal
   * fetches the same status on init, so a pre-check was the same request twice for one answer.
   */
  openDiscordMigrationHelp() {
    // Stamp the marker into the URL BEFORE opening. A signed-out member's next step is a login
    // round trip, and `redirectToConnect` builds `?returnto=` from the address bar — with a bare
    // URL they come back to a page that has nothing to reopen, which is the whole point of
    // having clicked. Awaited so `returnTo` on the modal carries it too.
    //
    // `_discordMigrationShown` is set first because adding the param fires a NavigationEnd,
    // which re-runs initView; without it the auto-open would stack a second modal on this one.
    this._discordMigrationShown = true;
    this._router
      .navigate([], {
        relativeTo: this._activatedRoute,
        queryParams: { discordMigration: "true" },
        queryParamsHandling: "merge",
        replaceUrl: true,
      })
      .then(() => this._openDiscordModal(DiscordClaimComponent, { showMigrationIntro: true }));
  }

  /**
   * Shared opener. Deliberately does NOT mark the community as set up, unlike the automatic
   * paths: clicking a link out of curiosity is not the same opt-in as following the migration
   * URL, and silently skipping the onboarding wizard on a stray click would be a surprise.
   */
  private _openDiscordModal(component: any, inputs: Record<string, any> = {}) {
    const ref = this.appService.showModal(component, { size: "lg", container: this.appService.appContainer });
    ref.componentInstance.returnTo = this._router.url;
    Object.keys(inputs).forEach((key) => (ref.componentInstance[key] = inputs[key]));
    // Dropping the markers on close stops the modal reopening on every refresh and leaves a
    // clean URL to share. `replaceUrl` so Back doesn't walk them into it again.
    //
    // No-op once the markers are gone: the modal can outlive this page — registering a product
    // from inside it navigates to that product, and claiming navigates to another — and a
    // relative navigation from a route we have already left would drag the member back here
    // just as they arrived somewhere else.
    const clear = () => {
      if (!/[?&](discordMigration|discordLinkSuccess|discord|reason)=/.test(this._router.url)) return;
      this._router.navigate([], {
        relativeTo: this._activatedRoute,
        queryParams: { discordMigration: null, discordLinkSuccess: null, discord: null, reason: null },
        queryParamsHandling: "merge",
        replaceUrl: true,
      });
    };
    ref.closed.subscribe(clear);
    ref.dismissed.subscribe(clear);
  }

  initBetaAnnouncements() {
    this.announcementsCollapsed = this.betaCommunityService.getAnnouncementsCollapsed(false);
    this.betaCommunityService.fetchBetaAnnouncements(this.betaStringId, this.fbService.collection("beta_community_posts")).then((result) => {
      this.betaAnnouncements = result;
      this.cd.detectChanges();
      // Announcements are fetched outside the feed, so their existing replies have to be pulled in
      // separately. Only announcements with comments opened can have any.
      const commentableIds = (result || []).filter((ann) => ann.allow_comments).map((ann) => ann.key);
      if (commentableIds.length) {
        this.webCommunityService.loadRepliesForPosts(commentableIds, "beta_community_posts").then(() => {
          this.webCommunityService.watchDisplayedPosts(commentableIds, "beta_community_posts", true);
          this.cd.detectChanges();
        });
      }
      // Restore scroll after announcements section has rendered (delay allows posts to load first)
      if (result?.length && this.appService.scrolledContentOffsets.community !== undefined) {
        this._win.setTimeout(() => {
          this.setScrollPosition(this.appService.scrolledContentOffsets.community);
        }, 350);
      }
    });
  }

  initTopics() {
    this.webCommunityService.getTopics().then((result) => {
      this.topics = result.filter((item) => !item.archived);
      let topics = result.filter((item) => !item.archived);

      let archived = result.filter((item) => item.archived);
      archived.sort((a, b) => (a.title > b.title ? 1 : -1));
      topics.sort((a, b) => (a.title > b.title ? 1 : -1));
      this.topics = [...topics].filter((item) => !item.limitToRole || this.userService.user?.[item.limitToRole]);
      this.allTopics = [...topics, ...archived].filter((item) => !item.limitToRole || this.userService.user?.[item.limitToRole]);
      if (this.displayedSection == "topics") {
        let topic = this.topicID ? this.topics.filter((item) => item.string_id.toLowerCase() == this.topicID.toLowerCase())[0] : false;
        this.showTopic(topic || (this.topics && this.topics.length > 0 ? this.topics[0] : null));
      }
    });
  }

  channelSelected(event) {
    this.displayedSection = "channels";
    this.selectedTopic = undefined;
    this.selectedChannel = this.betaService.communityTagMap[event] || this.webCommunityService.skillsMap[event] || this.webCommunityService.extraChannelsMap[event];
    this.communityService.selectedChannel = this.selectedChannel;
    // A channel tag clicked inside the open post — the post belongs to the old channel's feed.
    if (this.selectedPostId) this.closePostPane();
    this.cd.detectChanges();
  }

  scrollToTop() {
    this.contentContainer.nativeElement.scrollTop = 0;
  }

  postsLoaded(args?) {
    this.postsLoading = false;
    this.updateChannelsWithBadges();
    // The channel component exists by now, so its view mode is readable — this is the
    // earliest honest point to decide whether a pane is possible.
    this.updateSplitPaneLayout();
    if (args?.append) return;
    this._win.setTimeout(() => {
      // Returning from a post detail: land on the post that was opened. The saved pixel
      // offset is only ever approximately right — the feed re-renders on the way back and
      // row heights shift (a thumbnail resolves, an embed collapses), which in list view
      // is enough to drop the member several rows from where they left. Anchoring to the
      // row itself is exact, and both views render `[id]="post.id"` so it works for each.
      if (args?.scrollToPostId) {
        const el = document.getElementById(String(args.scrollToPostId));
        if (el) {
          el.scrollIntoView({ behavior: "instant" as ScrollBehavior, block: "center" });
          return;
        }
        // Post no longer in the list (deleted, or filtered out by a since-changed query) —
        // fall through to the offset so the feed at least doesn't jump to the top.
      }
      if (this.appService.scrolledContentOffsets.community !== undefined) {
        this.setScrollPosition(this.appService.scrolledContentOffsets.community);
      }
    }, 300);
  }

  filter() {
    this.filters = this.betaCommunityService.applyFilters(this.filters);
    this.appService.scrolledContentOffsets.community = undefined;
    this.updateQueryParams();

    // initPosts() routes to the Firestore recent-replies path when that sort is active;
    // Algolia is only used with a text query in that mode.
    const useRecentRepliesPath = this.betaStringId && !this.query && this.betaCommunityService.betaSortBy === "recentactivity";
    if (this.betaStringId && this.betaCommunityService.hasActiveFilters && !useRecentRepliesPath) {
      const algoliaFilters = this.betaCommunityService.buildAlgoliaFilters(this.betaStringId, this.selectedChannel, this.user?.id);
      this.ensureChannelViewAndRun(() => {
        this.channelCmp?.initPostsFromAlgolia(algoliaFilters, this.query);
      });
    } else {
      this.ensureChannelViewAndRun(() => {
        this.channelCmp?.initPosts();
      });
    }

    this.updateChannelsWithBadges();
  }

  updateQueryParams() {
    const queryParams: any = this.betaStringId ? this.betaCommunityService.getQueryParams(this.query) : {};
    if (!this.betaStringId && this.query) {
      queryParams.search = this.query;
    }

    // Everything above is rebuilt from scratch on purpose — that is how a filter or a search
    // that has been cleared disappears from the URL (see BetaCommunityService.getQueryParams,
    // which omits defaults deliberately). The cost is that any param this view does NOT own
    // is destroyed on the next section or channel change, which is why they are carried across
    // explicitly rather than by merging the whole query string.
    // Read from the ROUTER, not the address bar: `changeSection` calls `replaceState` with a
    // bare path moments before this runs, so by now `window.location.search` is already empty.
    // The router's snapshot is untouched by `replaceState` and still holds what the member
    // actually arrived with.
    const arrivedWith = this._activatedRoute.snapshot.queryParams || {};
    PRESERVED_QUERY_PARAMS.forEach((key) => {
      if (arrivedWith[key] != null) queryParams[key] = arrivedWith[key];
    });

    // Preserve the current route path
    const currentPath = this.location.path().split("?")[0];
    const url = this._router.createUrlTree([currentPath], { queryParams }).toString();

    this.location.replaceState(url);
    this.communityService.currentCommunityUrl = url;
  }

  buildFilters() {
    this.filters = this.betaCommunityService.buildFilters(this._activatedRoute.snapshot.queryParams);
    this.updateQueryParams();
  }

  resetFilters() {
    this.filters = this.betaCommunityService.resetFilters();
    this.query = "";
    this.updateQueryParams();
    this.ensureChannelViewAndRun(() => {
      this.channelCmp?.initPosts();
    });
  }

  closeTags() {
    this.showTags = false;
  }

  init() {
    if (this.webCommunityService.profile && this.webCommunityService.profile.status == "banned") {
      this._router.navigate(["../home"]);
    }
    if (this.betaStringId) {
      // When restoring from post detail, skip API calls — use cached tag data
      if (this.communityService.restoringFromPostDetail) {
        const cachedTags = Object.values(this.betaService.communityTagMap);
        if (cachedTags.length) {
          this.betaProgram = this.betaService.betaDetailByStringId[this.betaStringId];
          this.initBetaCommunityTags(cachedTags);
          this.displayedChannels = this.channels;
          this.updateChannelsWithBadges();
          this.loading = false;
          const urlChannelId = this._activatedRoute.snapshot.params["channelid"];
          const targetChannel =
            urlChannelId && this.displayedSection == "channels"
              ? this.channels.find(
                  (item) => item.id?.toString().toLowerCase() == urlChannelId.toString().toLowerCase() || item.string_id?.toString().toLowerCase() == urlChannelId.toString().toLowerCase()
                )
              : this.communityService.selectedChannel || this.channels[0];
          if (this._activatedRoute.snapshot.queryParams["search"]) {
            this.query = this._activatedRoute.snapshot.queryParams["search"];
          }
          this.showChannel(targetChannel || this.channels[0]);
          return;
        }
      }
      this.betaService.getBetas().subscribe((result) => {
        this.betaProgram = this.betaService.betaDetailByStringId[this.betaStringId];
        if (!this.betaProgram) {
          alert("A beta program with this ID was not found.");
          this._router.navigate(["../beta"]);
        } else if (!this.betaService.enrolledMap[this.betaProgram.string_id]) {
          alert("You are not enrolled in this beta.");
          this._router.navigate(["../beta"]);
        } else {
          this.betaService.getBetaCommunityTags(this.betaProgram.string_id).subscribe((result: any) => {
            this.initBetaCommunityTags(result);
            this.displayedChannels = this.channels;
            this.updateChannelsWithBadges();
            this.loading = false;
            const urlChannelId = this._activatedRoute.snapshot.params["channelid"];
            const targetChannel =
              urlChannelId && this.displayedSection == "channels"
                ? this.channels.find(
                    (item) => item.id?.toString().toLowerCase() == urlChannelId.toString().toLowerCase() || item.string_id?.toString().toLowerCase() == urlChannelId.toString().toLowerCase()
                  )
                : undefined;
            if (this._activatedRoute.snapshot.queryParams["search"]) {
              // Set query before showing channel so the channel component picks it up
              this.query = this._activatedRoute.snapshot.queryParams["search"];
              this.showChannel(targetChannel || this.channels[0]);
            } else if (targetChannel) {
              this.showChannel(targetChannel);
            } else {
              if (this.displayedSection == "channels") this.showChannel(this.channels[0]);
            }
          });
        }
      });
    } else {
      // A skills failure must not take the channels (and so the whole feed) down with it.
      const mySkills$ = this.user ? this.loadMySkills().pipe(catchError(() => of([]))) : of([]);
      // Every community — FSP included — now sources its channels from the per-community
      // `community_channels` table via `GET /communities/:id/channels`. FSP was migrated
      // by `paeapi/scripts/import-skills-to-community-channels.js`; the legacy
      // `GET /community/skills/` endpoint stays for user-profile skill lookups but is no
      // longer the sidebar source.
      const communityId = this.communityStringId || FSP_COMMUNITY_STRING_ID;
      // In parallel: the channels used to wait for the skills request to finish first, an
      // extra round trip in front of every feed load. `initSkills` reads `mySkills`, which
      // `loadMySkills` has set by the time both have landed.
      forkJoin([mySkills$, this.webCommunityService.getCommunityChannels(communityId)]).subscribe(([, result]: [any, any]) => {
        if (!this.user) this.mySkills = [];
        this.initSkills(result);
        this.updateChannelsWithBadges();
        this.loading = false;
        if (this._activatedRoute.snapshot.queryParams["search"]) {
          this.query = this._activatedRoute.snapshot.queryParams["search"];
          const targetChannel = this.channelID
            ? this.channels.find(
                (item) => item.id?.toString().toLowerCase() == this.channelID.toString().toLowerCase() || item.string_id?.toString().toLowerCase() == this.channelID.toString().toLowerCase()
              )
            : this.channels[0];
          if (targetChannel) this.showInitialChannel(targetChannel);
        } else if (this.channelID) {
          let channel = this.channels.filter(
            (item) => item.id?.toString().toLowerCase() == this.channelID.toString().toLowerCase() || item.string_id?.toString().toLowerCase() == this.channelID.toString().toLowerCase()
          )[0];
          if (channel) {
            this.showInitialChannel(channel);
          } else if (this.displayedSection == "channels") {
            // `channelID` can be a channel from somewhere else entirely: `initView` seeds it
            // from the service's `selectedChannel`, which outlives the community it was
            // picked in — arrive from a beta tag, or another community's channel, and it names
            // nothing here. Selecting nothing left no feed mounted, so `postsLoaded` never
            // fired and the skeleton stayed up until the member clicked a channel themselves.
            this.showInitialChannel(this.channels[0]);
          }
        } else {
          if (this.displayedSection == "channels") this.showInitialChannel(this.channels[0]);
        }

        if (this._activatedRoute.snapshot.queryParams["skill_id"]) {
          // Legacy query param support: redirect to new route
          this._router.navigate(["../community/connect", this._activatedRoute.snapshot.queryParams["skill_id"]]);
        }
        this.cd.detectChanges();
      });

      // lets not init topics for beta yet.
      this.initTopics();
    }
  }

  public postDetailRepliesSubscription: any;
  // --- Split-pane post detail -------------------------------------------------------

  /** Popstate listener — see `ngOnInit`. Held so it can be dropped on destroy. */
  private _locationSub: any = null;

  /**
   * Where the feed was scrolled to when a post took over the view full-width.
   *
   * Both scrollers, because which one is live depends on the layout: on the Fender shell
   * the DOCUMENT scrolls and `contentContainer` never moves (see `.community-split-pane`'s
   * note in `_community.scss`), while beta's `.content` section scrolls internally.
   *
   * Exact, unlike the row anchor it falls back to: the feed is only hidden while the post
   * is up, so nothing re-renders and nothing shifts. Null when we never took the reading —
   * the detailed view still routes to a post, which rebuilds this component.
   */
  private _feedScroll: { doc: number; container: number } | null = null;

  /**
   * Open a post from the list — beside the feed where the layout allows, full-width in
   * place of it where it doesn't.
   *
   * Either way the feed stays mounted (the full-width detail only `[hidden]`s it), so
   * neither opening nor closing refetches anything, and closing lands back on this
   * community rather than wherever a cached `returnUrl` happened to point.
   *
   * The URL is pushed with `location.go` so the post gets a real history entry — browser
   * back closes it and returns to the channel, which is what "one URL, one rendering"
   * buys. (`switchSection` uses `replaceState` because a section change is not somewhere
   * you should have to press back out of; opening a post is.) `location.go`, not
   * `router.navigate`: navigating would tear down and rebuild this component, losing the
   * feed we are rendering beside — and a second navigation to the SAME post URL is
   * ignored by the router, so a row would stop reopening after it had been closed once.
   */
  openSelectedPost(postId: string) {
    if (!postId) return;
    this.selectedPostId = String(postId);
    // Decides pane vs full-width from the layout, and sizes the pane when there is one.
    this.updateSplitPaneLayout();
    const url = this._router.createUrlTree(["/community", this.communityStringId, "post", postId]).toString();
    this.location.go(url);
    // The full-width detail replaces the feed in the flow, so without this the member
    // arrives partway down the post they just opened. Remember where the feed was first.
    if (this.showFullPageDetail) {
      this._captureFeedScroll();
      this._revealFullPageDetail();
    }
  }

  /** The post id in a `/community/<community>/post/<id>` URL, or null for anything else. */
  private _postIdFromUrl(url: string): string | null {
    const match = (url || "").split("?")[0].match(/^\/community\/[^/]+\/post\/([^/]+)$/);
    return match ? match[1] : null;
  }

  /** Close the pane and put the URL back on the channel. */
  closePostPane() {
    this.selectedPostId = null;
    this.showFullPageDetail = false;
    const channelId = this.selectedChannel?.string_id || this.channelID || "all_posts";
    this.location.go(this._router.createUrlTree(["/community", this.communityStringId, "channels", channelId]).toString());
  }

  /**
   * Back out of the full-width detail to the feed underneath it.
   *
   * Same close as the pane's, plus the scroll restore the pane doesn't need: opening the
   * post scrolled the page to it, so without this the feed comes back at the post's offset
   * rather than at the row it was opened from.
   */
  closeFullPageDetail() {
    // Opened from the hub: back to it, not to this community's own feed underneath.
    if (this._hubReturnUrl) {
      const url = this._hubReturnUrl;
      this._hubReturnUrl = null;
      this._router.navigateByUrl(url);
      return;
    }
    const postId = this.selectedPostId;
    this.closePostPane();
    this._restoreFeedScroll(postId);
  }

  private _captureFeedScroll() {
    this._feedScroll = {
      doc: typeof window !== "undefined" ? window.scrollY || 0 : 0,
      container: this.contentContainer?.nativeElement?.scrollTop || 0,
    };
  }

  /**
   * Put the post's own top under the nav.
   *
   * `scrollIntoView` rather than a scroll-to-zero because the shell's hero, search row and
   * channel header sit above the feed in the same scroller — zero would put the member at
   * the top of the PAGE, several hundred pixels above the post they just opened. The 90px
   * of clearance is `scroll-margin-top` on `.community-inline-detail`, so the fixed nav
   * doesn't cover the back button.
   */
  private _revealFullPageDetail() {
    this._win.setTimeout(() => {
      const el = this.fullPageDetail?.nativeElement;
      if (el) el.scrollIntoView({ behavior: "instant" as ScrollBehavior, block: "start" });
      else this.scrollToTop();
    });
  }

  /** Put the feed back where it was when the post took it over. */
  private _restoreFeedScroll(postId: string | null) {
    const exact = this._feedScroll;
    this._feedScroll = null;
    this._win.setTimeout(() => {
      if (exact) {
        if (this.contentContainer?.nativeElement) this.contentContainer.nativeElement.scrollTop = exact.container;
        if (typeof window !== "undefined") window.scrollTo({ top: exact.doc, behavior: "instant" as ScrollBehavior });
        return;
      }
      // No reading of our own — this component was rebuilt on the way to the post. Anchor on
      // the row instead, the way `postsLoaded` does when it restores from the back-cache.
      if (!postId) return;
      const el = document.getElementById(String(postId));
      if (el) el.scrollIntoView({ behavior: "instant" as ScrollBehavior, block: "center" });
    });
  }

  /**
   * Match the open post to what the address bar says, WITHOUT touching history.
   *
   * Only for URL changes this component didn't make — browser back / forward. Everything
   * `openSelectedPost` and `closePostPane` do is repeated here minus the `location.go`, since
   * pushing a new entry while handling a pop is how you build a history loop nobody can
   * escape.
   */
  private _syncPostFromUrl(url: string) {
    const postId = this._postIdFromUrl(url);
    if (postId === this.selectedPostId) return;
    const previous = this.selectedPostId;
    this.selectedPostId = postId;
    this.updateSplitPaneLayout();
    if (postId) {
      if (this.showFullPageDetail) {
        this._captureFeedScroll();
        this._revealFullPageDetail();
      }
    } else {
      this._restoreFeedScroll(previous);
    }
    this.cd.detectChanges();
  }

  /**
   * The member flipped the feed between list and detailed.
   *
   * Leaving LIST with a post open in the pane closes it and puts the URL back on the
   * channel. Without this the post would be promoted to a full-width detail — technically
   * consistent with "no pane means full width", but not what asking for the detailed feed
   * meant: they wanted the feed, and would have had to press back to reach it.
   *
   * The reverse is left alone on purpose: flipping list-ward while reading a post moves that
   * same post into the pane, which is a continuation rather than a surprise.
   */
  onViewModeChanged(mode: string) {
    if (mode !== "list" && this.selectedPostId) this.closePostPane();
    this.updateSplitPaneLayout();
  }

  /**
   * Recompute whether a pane is even possible, and close it if it is not.
   *
   * Runs on mount, on resize and whenever the feed's layout toggle changes. Beta is
   * excluded: it has its own list layout and its own sidebar, and no post-URL route that
   * resolves to this shell.
   */
  updateSplitPaneLayout() {
    const wide = typeof window !== "undefined" && window.innerWidth >= this.SPLIT_MIN_VIEWPORT;
    // Read the stored view mode, NOT `channelCmp.displayType`. The channel component may not
    // exist yet (first paint) or may be hidden behind a full-width detail — and deriving this
    // from a child that only appears once the feed has loaded created a deadlock: with the
    // detail up, `postsLoaded` never fired, so the pane could never become possible again.
    const listView = this.webCommunityService.getCommunityViewMode(this.communityStringId) === "list";
    this.splitPaneActive = !this.betaStringId && wide && listView;
    this.showFullPageDetail = !!this.selectedPostId && !this.splitPaneActive;
    if (this.splitPaneActive) this.ensurePaneWidth();
    // A full-width feed is never compact, whatever the last drag left behind.
    else this.compactList = false;
  }

  /**
   * Seed the pane to half the available row the first time, then respect the member's
   * stored choice. Clamped on every read because the window may be narrower than it was
   * when the width was saved.
   */
  private ensurePaneWidth() {
    const stored = this.webCommunityService.getCommunityPaneWidth();
    const rowRight = this._splitRowRect()?.right ?? 0;
    // Half of what feed+pane actually share — halving the whole row handed the pane half of
    // the channels rail's width as well.
    const seeded = stored || Math.round((rowRight - this._feedLeft()) / 2);
    this.paneWidth = this._clampPaneWidth(seeded);
  }

  /** Drag handler — the divider reports a viewport x, which becomes the pane's width. */
  onPaneResize(clientX: number) {
    const row = this._splitRowRect();
    if (!row) return;
    this.paneWidth = this._clampPaneWidth(row.right - clientX);
    this.webCommunityService.setCommunityPaneWidth(this.paneWidth);
  }

  /** Keyboard resize, so the divider isn't pointer-only. */
  onDividerKeydown(event: KeyboardEvent) {
    const step = event.shiftKey ? 64 : 16;
    if (event.key === "ArrowLeft") this.paneWidth = this._clampPaneWidth(this.paneWidth + step);
    else if (event.key === "ArrowRight") this.paneWidth = this._clampPaneWidth(this.paneWidth - step);
    else return;
    event.preventDefault();
    this.webCommunityService.setCommunityPaneWidth(this.paneWidth);
  }

  private _splitRowRect(): DOMRect | null {
    const row = this.communityColumnLayoutContainer?.nativeElement?.querySelector(".fui-sidebar-content-container");
    return row ? row.getBoundingClientRect() : null;
  }

  private _splitRowWidth(): number {
    return this._splitRowRect()?.width || (typeof window !== "undefined" ? window.innerWidth : 1200);
  }

  /**
   * Where the feed column starts. Everything left of this — the channels rail and the
   * layout's padding — is not the pane's to take.
   */
  private _feedLeft(): number {
    const feed = this.contentContainer?.nativeElement?.getBoundingClientRect();
    return feed ? feed.left : (this._splitRowRect()?.left ?? 0);
  }

  /**
   * Keep both columns usable.
   *
   * The upper bound is measured from the FEED's left edge, not the row's. Using the row
   * meant the 330px channels rail counted as feed space, so dragging could squeeze the
   * actual list down to a few dozen pixels while the arithmetic still thought it had 420.
   */
  private _clampPaneWidth(width: number): number {
    const rowRight = this._splitRowRect()?.right ?? (typeof window !== "undefined" ? window.innerWidth : 1200);
    const available = rowRight - this._feedLeft();
    const max = Math.max(this.PANE_MIN_WIDTH, available - this.FEED_MIN_WIDTH);
    const clamped = Math.round(Math.min(Math.max(width, this.PANE_MIN_WIDTH), max));
    // Recomputed here because this runs on every drag frame and on every resize — the one
    // place that always knows the feed's final width.
    this.compactList = available - clamped < this.FEED_COMPACT_WIDTH;
    return clamped;
  }

  showPostDetail(postId?) {
    this.communityColumnLayoutContainer.nativeElement.classList.add("blurred");
    this.shouldShowPostDetails = true;
    this.webCommunityService
      .getPostByID(postId || this.postID)
      .then((result: any) => {
        let parentPostID = postId || this.postID;
        if (result[0] && result[0].parentID) {
          parentPostID = result[0].parentID;
          this.webCommunityService
            .getPostByID(parentPostID)
            .then((result: any) => {
              this.postDetail = result[0];
              this.postDetailReplies = result.filter((item) => item.parentID);
            })
            .catch((err) => {});
        } else {
          this.postDetail = result[0];
          this.postDetailReplies = result.filter((item) => item.parentID);
        }

        this.postDetailRepliesSubscription = this.webCommunityService
          .watchPostReplies(parentPostID, this.betaStringId ? "beta_community_posts" : this.env.communityFirestoreCollection)
          .subscribe((result) => {
            this.postDetailReplies = result;
          });
      })
      .catch((error) => {
        this.appService.alertError(error);
        this.closePostDetails(false, true);
      });
  }

  closePostDetails(args, force) {
    if (force || (args && args.target && args.target.className && args.target.className.indexOf && args.target.className.indexOf("post-detail-overlay") > -1)) {
      this.communityColumnLayoutContainer.nativeElement.classList.remove("blurred");
      this.postDetailRepliesSubscription.unsubscribe();
      this.shouldShowPostDetails = false;
      this.postDetailReplies = undefined;
      this.postDetail = undefined;
      // this._router.navigate(["../community/channels"]);
    }
  }

  search() {
    this.selectedQuery = this.query;
    this.appService.scrolledContentOffsets.community = undefined;
    if (this.betaStringId) {
      const algoliaFilters = this.betaCommunityService.buildAlgoliaFilters(this.betaStringId, this.selectedChannel, this.user?.id);
      this.updateQueryParams();
      this.ensureChannelViewAndRun(() => {
        this.channelCmp?.initPostsFromAlgolia(algoliaFilters, this.query);
      });
    } else if (this.query) {
      // Regular community — use Algolia via channel component
      this.updateQueryParams();
      this.ensureChannelViewAndRun(() => {
        this.channelCmp?.initPostsFromAlgolia(this.channelCmp.buildCommunityAlgoliaFilters(this.selectedChannel), this.query);
      });
    } else {
      // Empty query — return to unfiltered browse
      this.updateQueryParams();
      if (this.channelCmp) this.channelCmp.searchQuery = "";
      this.ensureChannelViewAndRun(() => {
        this.channelCmp?.initPosts();
      });
    }
  }

  clear() {
    this.communityService.cachedSearchPage = undefined;
    this.communityService.cachedSearchParentsMap = undefined;
    this.communityService.cachedSearchResults = undefined;
    this.query = "";
    this.appService.scrolledContentOffsets.community = undefined;
    this.updateQueryParams();
    // Eagerly clear searchQuery on the channel component since Angular change detection
    // hasn't propagated the Input binding yet when initPosts reads it.
    if (this.channelCmp) this.channelCmp.searchQuery = "";
    const useRecentRepliesPath = this.betaStringId && this.betaCommunityService.betaSortBy === "recentactivity";
    if (this.betaStringId && this.betaCommunityService.hasActiveFilters && !useRecentRepliesPath) {
      const algoliaFilters = this.betaCommunityService.buildAlgoliaFilters(this.betaStringId, this.selectedChannel, this.user?.id);
      this.ensureChannelViewAndRun(() => {
        this.channelCmp?.initPostsFromAlgolia(algoliaFilters);
      });
    } else {
      this.ensureChannelViewAndRun(() => {
        this.channelCmp?.initPosts();
      });
    }
  }

  /**
   * Ensures displayedSection is "channels" (so channelCmp is rendered), then runs the callback.
   * If already in channels view, runs immediately. Otherwise switches and waits for Angular to render.
   */
  private ensureChannelViewAndRun(callback: () => void) {
    if (this.displayedSection === "channels" && this.channelCmp) {
      callback();
    } else {
      this.displayedSection = "channels";
      if (!this.selectedChannel) {
        this.selectedChannel = this.channels?.find((c) => c.string_id === "all_posts") || this.channels?.[0];
        this.communityService.selectedChannel = this.selectedChannel;
      }
      this._win.setTimeout(() => {
        callback();
      });
    }
  }

  private getScrollPosition(): number {
    // For beta community pages, the .content section has overflow:visible (CSS override when fui-footer is present),
    // so the page itself scrolls rather than the content section.
    if (this.betaStringId) return window.scrollY;
    return this.contentContainer?.nativeElement?.scrollTop || 0;
  }

  private setScrollPosition(pos: number) {
    if (this.betaStringId) {
      window.scrollTo({ top: pos, behavior: "instant" });
    } else if (this.contentContainer?.nativeElement) {
      this.contentContainer.nativeElement.scrollTop = pos;
    }
  }

  contentContainerScrolled(args) {
    this.appService.scrolledContentOffsets.community = this.contentContainer.nativeElement.scrollTop;
  }

  /**
   * Mount the feed the page arrived on, once the channels have loaded.
   *
   * Not the member choosing a channel, so a post the URL opened stays open, and the URL stays
   * on it. Going through plain `showChannel` closed it: arriving at /community/<id>/post/<pid>
   * from an email or a share link showed the post for as long as the channels took to load,
   * then dropped the member on All Posts.
   */
  private showInitialChannel(channel) {
    this.showChannel(channel, false, true);
  }

  showChannel(channel, clearScroll = false, keepOpenPost = false) {
    this.selectedTopic = undefined;
    if (clearScroll) {
      this.appService.scrolledContentOffsets.community = 0;
      this.setScrollPosition(0);
    }

    // The open post belongs to the feed being left. No `closePostPane()` here: `switchSection`
    // already rewrites the URL to the new channel, and a `location.go` first would leave a
    // stray history entry for the old one.
    const keepPost = keepOpenPost && !!this.selectedPostId;
    if (this.selectedPostId && !keepPost) {
      this.selectedPostId = null;
      this.showFullPageDetail = false;
    }
    this.switchSection("channels", channel, undefined, { keepUrl: keepPost });
    // Opening a channel clears nothing: its badge drops as you read its threads.
    if (channel?.id) {
      const page = this.avoService.getPageProperties({ pageName: "Community Detail" });
      // `channel.title` is a per-locale map since the DENG-728 channel migration; resolve
      // it to a string before handing it to Avo, otherwise the analytics event carries an
      // object and Avo's string-typed `channelName` breaks the payload.
      const lang = this.languageService.selectedLanguage?.code || "en";
      const rawChannelName = resolveLocalizedString(channel.title, lang, "") || channel.name;
      const channelName = Array.isArray(rawChannelName) ? rawChannelName.join(",") : rawChannelName;
      const extra = {
        name: "Community Detail",
        channelName,
        interactionType: "viewed",
      };
      const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
      const te: any = this.avoService.trackEvent();
      // Fire Loaded a Page for community channel views only for My Fender
      if (this.userService.appId === "fender" && te && typeof te.loadedAPage === "function") {
        te.loadedAPage({ ...page, customDestinationPageName_: "Loaded A Page" });
      }
      if (te && typeof te.communityViewed === "function") {
        te.communityViewed(payload);
      }
    }
  }

  showTopic(topic, clearScroll = false) {
    this.selectedChannel = undefined;
    if (clearScroll) {
      this.appService.scrolledContentOffsets.community = 0;
      this.setScrollPosition(0);
    }
    this.switchSection("topics", false, topic);
    if (topic) {
      const page = this.avoService.getPageProperties({ pageName: "Community Detail" });
      const rawName = topic.title || topic.name;
      const topicName = Array.isArray(rawName) ? rawName.join(",") : rawName;
      const extra = {
        name: "Community Detail",
        channelName: topicName,
        interactionType: "viewed",
      };
      const payload = this.avoService.buildEventPayload<Record<string, any>>(page, extra, { omitNulls: true });
      const te: any = this.avoService.trackEvent();
      // Fire Loaded a Page for community topic views only for My Fender
      if (this.userService.appId === "fender" && te && typeof te.loadedAPage === "function") {
        te.loadedAPage({ ...page, customDestinationPageName_: "Loaded A Page" });
      }
      if (te && typeof te.communityViewed === "function") {
        te.communityViewed(payload);
      }
    }
  }

  showMemberResults(results) {
    this.communityColumnLayoutContainer.nativeElement.classList.add("blurred");
    this.searchResults = results;
    this.shouldShowMemberResults = true;
  }

  closeMemberResults() {
    this.communityColumnLayoutContainer.nativeElement.classList.remove("blurred");
    this.shouldShowMemberResults = false;
    this._router.navigate(["../community/connect"]);
  }

  /**
   * `keepUrl` leaves the address bar alone — for a post that is open over the feed, whose URL
   * is the post's own rather than the channel's.
   */
  switchSection(section, channel?, topic?, opts: { keepUrl?: boolean } = {}) {
    // Handle string channel IDs (for built-in filters "all_posts", "my_posts", "pinned_posts")
    if (section == "connect" && !this.userService.user) {
      this.appService.promptSimpleSignup(SimpleSignupComponent);
      return;
    }
    if (channel == "all_posts" || channel == "pinned_posts" || channel == "my_posts") {
      channel = this.channels?.find((item) => item.string_id == channel || item.id == channel) || channel;
    }
    this.displayedSection = section;
    // Build the community-scoped base. As of 2026-08-31 FSP uses the same
    // `community/:communityStringId/...` URL shape as every other community — the legacy
    // `/community/channels/...` shape survives only as redirect stubs in the routes module.
    // Beta continues to use its own `/beta/:betaStringId/...` prefix.
    const communityId = this.communityStringId || FSP_COMMUNITY_STRING_ID;
    const base = this.betaStringId ? "beta/" + this.betaStringId : "community/" + communityId;
    let route = base + "/" + section;
    if (channel) {
      // Prefer `string_id` — DynamoDB `community_channels` items don't carry a numeric `id`.
      // The old MySQL rows had `id: number`; guard both shapes for safety.
      const channelSeg = String(channel.string_id ?? channel.id ?? "").toLowerCase();
      if (channelSeg) route = base + "/" + section + "/" + channelSeg;
    } else if (topic) {
      this.loading = false;
      route = base + "/" + section + "/" + topic.string_id.toLowerCase();
    }
    if (section != "search" && !opts.keepUrl) {
      this.location.replaceState(route);
      // Also update AVO's internal navigation tracker so referrer between Community tabs is correct
      const navPath = route.startsWith("/") ? route : "/" + route;
      this.avoService.recordNavigation(navPath);
    }

    if (channel) {
      this.selectedTopic = undefined;
      this.selectedChannel = channel;
      this.communityService.selectedChannel = channel;
    } else if (topic) {
      this.selectedChannel = undefined;
      this.selectedTopic = topic;
    }
    this.updateQueryParams();
    this.updateCommunityTitle();
  }

  private updateCommunityTitle() {
    let page = "Community";

    if (this.displayedSection === "channels") {
      page = "Community – Channels";
    } else if (this.displayedSection === "topics") {
      page = "Community – Topics";
    } else if (this.displayedSection === "connect") {
      page = "Community – Connect";
    }

    // Once the community config has resolved, `applyCommunitySeoMeta()` owns `<title>` — it has
    // set the real search title ("Tone Master Pro Community | MyFender"). Calling setPageTitle()
    // here would clobber it with a generic route label that is identical across every community,
    // which is exactly what shipped in Phase 1 and degraded every prerendered snapshot.
    // `trackPageLoaded()` keeps the "Loaded A Page" event firing without the title write.
    //
    // Before the config resolves (and on beta / non-public builds, where no SEO meta is emitted
    // at all) fall back to the old behaviour so the tab never sits on the previous page's title.
    if (this.seoOwnsPageTitle() && this.communityConfigLoaded) {
      this.appService.trackPageLoaded(page);
    } else {
      this.appService.setPageTitle(page);
    }
  }

  /**
   * Whether `applyCommunitySeoMeta()` is the owner of `<title>` for the current view.
   * Beta communities are private and never emit SEO meta; the whole public-community SEO
   * path sits behind the `communityPublicAccess` flag. Kept as one predicate so the emitter
   * and `updateCommunityTitle()` can never disagree about who writes the title.
   */
  private seoOwnsPageTitle(): boolean {
    if (this.betaStringId) return false;
    return !!(environment.features as any).communityPublicAccess;
  }

  /**
   * Open the per-community settings modal (DENG-733, widened in DENG-847). Wired to the
   * gear icon in the layout header. Runs against `this.communityStringId` — always
   * populated by `<myp-community>`'s input binding (defaults to FSP).
   */
  openCommunitySettings() {
    const modalRef = this.appService.showModal(CommunitySettingsComponent, { size: "md" });
    modalRef.componentInstance.communityStringId = this.communityStringId;
    modalRef.componentInstance.communityTitle = this.communityHeading;
  }

  /**
   * The sole filter-toggle for the actions row (2026-09-01, matching `/communities`
   * pattern). `<fui-content-filter>`'s built-in Show/Hide Filters text button was
   * replaced by this hamburger circle; the button now runs on every viewport, not
   * just mobile.
   *
   * - Desktop (≥800px): toggles `showTags` so the inline sidebar renders / hides.
   * - Mobile (<800px): opens the sidebar as a modal via `showMobileModal()`.
   *
   * The mobile branch deliberately does NOT touch `showTags` — an earlier version
   * did, and it created a race: flipping `showTags` unmounts the placeholder DOM
   * that held the projected `filterContent`, and the synchronous `showMobileModal()`
   * call that follows grabs the now-empty ViewChild reference, opening an empty
   * modal. Keeping the two branches disjoint avoids the race.
   */
  toggleFilters() {
    // The same breakpoint the rail's own `display` uses — below it there is no inline rail
    // to toggle, so the button has to open the modal or it does nothing at all.
    const isMobile = typeof window !== "undefined" && window.innerWidth < this.SPLIT_MIN_VIEWPORT;
    if (isMobile) {
      this.fuiSidebarFilter?.showMobileModal();
    } else {
      this.showTags = !this.showTags;
    }
  }
  selectBetaView(view) {
    this.selectedBetaView = view;
    this.betaCommunityService.setViewMode(view);
    this.updateBetaViewOptions();
  }

  toggleAnnouncementsCollapsed() {
    this.announcementsCollapsed = !this.announcementsCollapsed;
    this.betaCommunityService.setAnnouncementsCollapsed(this.announcementsCollapsed);
  }

  /**
   * Wizard emitted `(complete)` — dismiss the overlay and drop the user into the feed.
   * The wizard has already written `hasSetupCommunity: true` to their profile, so a
   * mid-session re-mount won't re-open it.
   */
  onOnboardingWizardComplete() {
    this.inSetupMode = false;
    this.cd.detectChanges();
  }

  getChannelLabel(channel: any): string {
    const prefix = channel.string_id != "all_posts" && channel.string_id != "pinned_posts" && channel.string_id != "my_posts" ? "#" : "";
    const label = this.betaStringId ? channel.title : channel.translatedLabel || "com.skills." + channel.string_id;
    return prefix + label;
  }

  shouldShowChannel(channel: any): boolean {
    return true;
  }

  onChannelSelected(channelFilterItem: FilterItem): void {
    this.appService.mobileMenuShown = false;
    // Find the actual channel object from displayedChannels first, then fallback to channels
    let channel = this.displayedChannels?.find((ch) => ch.string_id === channelFilterItem.id || ch.id === channelFilterItem.id || ch.id?.toString() === channelFilterItem.id?.toString());
    if (!channel) {
      // Fallback: try to find in all channels (for special channels like "pinned_posts", "my_posts" that might not be in displayedChannels)
      channel = this.channels?.find((ch) => ch.string_id === channelFilterItem.id || ch.id === channelFilterItem.id || ch.id?.toString() === channelFilterItem.id?.toString());
    }
    if (channel) {
      this.showChannel(channel, true);
    } else {
      // Last resort: pass the id as string (for built-in filters "all_posts", "my_posts", "pinned_posts")
      this.showChannel(channelFilterItem.id, true);
    }
    this.appService.closeModals();
  }

  /** Channel ids the unread query and "Mark all read" operate on. */
  private _railChannelIds(): Array<string> {
    return (this.displayedChannels || []).map((channel) => String(channel?.string_id || channel?.id || "")).filter((id) => !!id);
  }

  /**
   * Fetch what the rail's badges count from, once per community — see
   * `WebCommunityService.getCommunityUnreadCandidates`. Reading then recounts it locally.
   *
   * Beta is excluded: its rail already carries tag counts, which mean something else entirely.
   */
  private refreshChannelUnread(force = false) {
    if (this.betaStringId) return;
    const ids = this._railChannelIds();
    if (!ids.length) return;
    if (!force && this._unreadFetchedFor === this.communityStringId) return;
    // The read floor lives on the profile, which arrives asynchronously; without it there is
    // nothing honest to count against. Left unset so the next call retries.
    if (!this.webCommunityService.profile) {
      this._unreadFetchedFor = null;
      return;
    }
    const communityStringId = this.communityStringId;
    this._unreadFetchedFor = communityStringId;
    this.webCommunityService.getCommunityUnreadCandidates(communityStringId, ids).then((candidates) => {
      if (communityStringId !== this.communityStringId) return;
      this._unreadCandidates = candidates;
      this._applyChannelUnread();
    });
  }

  /** The badges as they stand: the fetched threads against the read floor and your view records. */
  private _applyChannelUnread() {
    if (this.betaStringId || !this._unreadCandidates) return;
    const counts = this.webCommunityService.summarizeCommunityUnread(this.communityStringId, this._unreadCandidates).channels;
    const before = this.channelUnread;
    const same = Object.keys(counts).length === Object.keys(before).length && Object.keys(counts).every((id) => counts[id] === before[id]);
    if (same) return;
    this.channelUnread = counts;
    this.hasUnreadChannels = Object.keys(counts).length > 0;
    this.cd.detectChanges();
  }

  /** Everything in this community read: the floor moves to now, which the recount (on `postViews$`) applies. */
  markAllChannelsRead() {
    if (!this._railChannelIds().length) return;
    this.webCommunityService.setCommunityReadFloor(this.communityStringId, Date.now());
    this.channelUnread = {};
    this.hasUnreadChannels = false;
  }

  private updateChannelsWithBadges() {
    this.refreshChannelUnread();
    if (!this.displayedChannels) {
      this.channelsWithBadges = [];
      return;
    }

    this.channelsWithBadges = this.displayedChannels.map((channel): FilterItem => {
      const prefix = channel.string_id != "all_posts" && channel.string_id != "pinned_posts" && channel.string_id != "my_posts" ? "#" : "";

      let baseLabel: string;
      if (this.betaStringId) {
        baseLabel = channel.title || channel.display_name || "";
      } else {
        const stringId = channel.string_id || channel.id || "all_posts";
        baseLabel = channel.translatedLabel || "com.skills." + stringId;
      }

      const labelWithPrefix = prefix + baseLabel;

      const channelWithBadge: FilterItem = {
        id: channel.string_id || channel.id || channel.title,
        label: labelWithPrefix,
      };

      // Add badge value
      if (this.betaStringId && this.communityService.betaTagCountMap && this.communityService.betaTagCountMap[channel.string_id]) {
        const shouldShowBadge = channel.string_id != "all_posts" && channel.string_id != "pinned_posts" && channel.string_id != "my_posts";
        channelWithBadge.badgeValue = shouldShowBadge ? this.communityService.betaTagCountMap[channel.string_id] : null;
        channelWithBadge.badgeClass = "badge";
      }
      return channelWithBadge;
    });
  }

  goToFeatureRequests() {
    window.open(this.env.fenderFeatureRequestsUrl);
  }

  ngOnDestroy() {
    if (typeof window !== "undefined") window.removeEventListener("resize", this._onWindowResize);
    if (this._locationSub) {
      this._locationSub.unsubscribe();
      this._locationSub = null;
    }

    this.appService.scrolledContentOffsets.community = this.getScrollPosition();
    super.ngOnDestroy();
  }

  // -- Hero nav mode -----------------------------------------------------------------
  //
  // Hoisted to `<fui-layout-header>` on 2026-09-01. When we pass `[heroImageUrl]="X"`
  // to the layout-header, it now handles `body.ghost-nav-route` + `appService.showGhostNav`
  // + the capture-phase scroll listener that toggles nav transparency past a threshold —
  // see `syncHeroNavMode()` in `layout-header.component.ts`. No community-specific logic
  // needed anymore.
}
